<script setup lang="ts">
import { computed } from "vue";
import { Wind } from "@lucide/vue";
import {
  getWeatherIconUrl,
  isPrecipitationWeatherCode,
  precipPopThreshold,
} from "@/utils/weatherCodes";

interface Props {
  weatherCode: number;
  windSpeed?: number;
  pop?: number;
  isDay?: boolean;
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  windSpeed: 0,
  pop: 0,
  isDay: true,
  size: 34,
});

// Get the icon URL based on weather code
const iconUrl = computed(() =>
  getWeatherIconUrl(props.weatherCode, props.isDay),
);

// Should we show the precipitation probability?
const shouldShowPop = computed(() => {
  return isPrecipitationWeatherCode(props.weatherCode) && props.pop > 0;
});

// Should we show wind indicator?
const showWindIndicator = computed(() => props.windSpeed > 20);

// PoP label styling
const popColor = computed(() => {
  return props.pop >= 60 ? "var(--pop-high-ink)" : "var(--pop-ink)";
});

const popFontWeight = computed(() => {
  return props.pop >= precipPopThreshold ? "700" : "500";
});

// Container height includes space for PoP label
const containerHeight = computed(() => {
  return `calc(var(--weather-symbol-size, ${props.size}px) + ${shouldShowPop.value ? 14 : 0}px)`;
});
</script>

<template>
  <div
    class="relative flex flex-col items-center"
    :style="{
      width: `var(--weather-symbol-size, ${size}px)`,
      height: containerHeight,
    }"
  >
    <!-- Probability of Precipitation Label -->
    <span
      v-if="shouldShowPop"
      class="z-10 text-[11px] leading-none"
      :style="{
        fontSize: 'var(--weather-pop-size, 11px)',
        color: popColor,
        fontWeight: popFontWeight,
      }"
    >
      {{ Math.round(pop) }}%
    </span>

    <!-- Weather Icon Container -->
    <div
      class="relative flex items-center justify-center"
      :style="{
        width: `var(--weather-symbol-size, ${size}px)`,
        height: `var(--weather-symbol-size, ${size}px)`,
        marginTop: shouldShowPop ? '-4px' : '0',
      }"
    >
      <!-- Main Weather Icon -->
      <img
        :src="iconUrl"
        :width="size"
        :height="size"
        class="h-full w-full drop-shadow-[0_1px_1px_#19212c75] dark:drop-shadow-[1px_1px_2px_#0008]"
        alt=""
      />

      <!-- Wind Indicator -->
      <Wind
        v-if="showWindIndicator"
        class="absolute -top-1 -right-2 text-text-secondary"
        :size="size * 0.4"
      />
    </div>
  </div>
</template>
